import { useState, useEffect } from 'react';
import {
  useApprovalQueue,
  useApprovalDetail,
  useApproveMessage,
  useRejectMessage,
  useEditMessage,
  useRegenerateMessage,
  useSuppressContact,
} from '../hooks/useApprovalQueue';
import { ScoreBadge } from '../components/ScoreBadge';
import { EvidenceCard } from '../components/EvidenceCard';
import { ConfirmModal } from '../components/ConfirmModal';
import type { ApprovalItem } from '../types';

// ---------------------------------------------------------------------------
// Approval list item
// ---------------------------------------------------------------------------

function QueueItem({
  item,
  isSelected,
  onClick,
}: {
  item: ApprovalItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        borderBottom: '1px solid #f3f4f6',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#eff6ff' : '#fff',
        borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
        border: 'none',
        borderLeftWidth: '3px',
        borderLeftStyle: 'solid',
        borderLeftColor: isSelected ? '#2563eb' : 'transparent',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: '#f3f4f6',
        transition: 'all 0.1s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{item.companyName}</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
            {item.decisionMaker?.title ?? 'Unknown role'}
          </div>
        </div>
        <ScoreBadge score={item.scores.overall} size="sm" />
      </div>
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
        <ScoreBadge score={item.scores.icp} label="ICP" size="sm" />
        <ScoreBadge score={item.scores.pain} label="Pain" size="sm" />
        <ScoreBadge score={item.scores.intent} label="Intent" size="sm" />
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: '4px',
            backgroundColor: item.contactVerified ? '#d1fae5' : '#fef3c7',
            color: item.contactVerified ? '#065f46' : '#92400e',
          }}
        >
          {item.contactVerified ? '✓' : '~'} Email
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Action button
// ---------------------------------------------------------------------------

type BtnVariant = 'green' | 'blue' | 'gray' | 'red' | 'darkred';

function ActionButton({
  label,
  variant,
  onClick,
  disabled,
}: {
  label: string;
  variant: BtnVariant;
  onClick: () => void;
  disabled?: boolean;
}) {
  const styles: Record<BtnVariant, { bg: string; color: string; border: string }> = {
    green: { bg: '#16a34a', color: '#fff', border: '#15803d' },
    blue: { bg: '#2563eb', color: '#fff', border: '#1d4ed8' },
    gray: { bg: '#6b7280', color: '#fff', border: '#4b5563' },
    red: { bg: '#dc2626', color: '#fff', border: '#b91c1c' },
    darkred: { bg: '#7f1d1d', color: '#fca5a5', border: '#991b1b' },
  };
  const s = styles[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 18px',
        borderRadius: '7px',
        border: `1px solid ${s.border}`,
        backgroundColor: disabled ? '#e5e7eb' : s.bg,
        color: disabled ? '#9ca3af' : s.color,
        fontSize: '14px',
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        letterSpacing: '0.02em',
        transition: 'opacity 0.1s',
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Score row
// ---------------------------------------------------------------------------

function ScoreRow({ overall, icp, pain, timing, evidence, intent }: {
  overall: number; icp: number; pain: number; timing: number; evidence: number; intent: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        padding: '14px 16px',
        backgroundColor: '#0f172a',
        borderRadius: '8px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
        <ScoreBadge score={overall} size="lg" />
        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600 }}>OVERALL</span>
      </div>
      <div style={{ width: '1px', height: '36px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
      {[
        { label: 'ICP', score: icp },
        { label: 'PAIN', score: pain },
        { label: 'TIMING', score: timing },
        { label: 'EVIDENCE', score: evidence },
        { label: 'INTENT', score: intent },
      ].map(({ label, score }) => (
        <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
          <ScoreBadge score={score} size="md" />
          <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel (right side)
// ---------------------------------------------------------------------------

type ModalType = 'approve' | 'reject' | 'suppress' | null;

function DetailPanel({ approvalId, onDismiss }: { approvalId: string; onDismiss: () => void }) {
  const { data: detail, isLoading, isError } = useApprovalDetail(approvalId);
  const approve = useApproveMessage();
  const reject = useRejectMessage();
  const edit = useEditMessage();
  const regen = useRegenerateMessage();
  const suppress = useSuppressContact();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [modal, setModal] = useState<ModalType>(null);
  const [editMode, setEditMode] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (detail) {
      setSubject(detail.outreachSubject);
      setBody(detail.outreachBody);
      setEditMode(false);
    }
  }, [detail?.id]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#9ca3af' }}>
        Loading…
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#991b1b' }}>
        Failed to load details.
      </div>
    );
  }

  const isBusy =
    approve.isPending || reject.isPending || regen.isPending || suppress.isPending || edit.isPending;

  async function handleApprove() {
    await approve.mutateAsync({ id: approvalId });
    setModal(null);
    showToast(`✓ Email approved and queued for ${detail!.companyName}`);
    onDismiss();
  }

  async function handleReject() {
    await reject.mutateAsync({ id: approvalId, reason: rejectReason });
    setModal(null);
    showToast('Prospect rejected.');
    onDismiss();
  }

  async function handleEdit() {
    await edit.mutateAsync({ id: approvalId, subject, body });
    setEditMode(false);
    showToast('Edits saved and re-queued.');
  }

  async function handleRegenerate() {
    await regen.mutateAsync(approvalId);
    showToast('Regeneration requested. Check back in ~30 seconds.');
    onDismiss();
  }

  async function handleSuppress() {
    await suppress.mutateAsync(approvalId);
    setModal(null);
    showToast('Contact suppressed permanently.');
    onDismiss();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            zIndex: 100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            whiteSpace: 'nowrap',
          }}
        >
          {toast}
        </div>
      )}

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 800, color: '#111827' }}>
                {detail.companyName}
              </h2>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {detail.domain}
                {detail.industry && ` · ${detail.industry}`}
                {detail.employees && ` · ${detail.employees} employees`}
                {detail.hq && ` · ${detail.hq}`}
              </div>
            </div>
          </div>
        </div>

        {/* Scores */}
        <ScoreRow
          overall={detail.scores.overall}
          icp={detail.scores.icp}
          pain={detail.scores.pain}
          timing={detail.scores.timing}
          evidence={detail.scores.evidence}
          intent={detail.scores.intent}
        />

        {/* Decision Maker & Contact */}
        {(detail.decisionMaker || detail.contact) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '18px' }}>
            {detail.decisionMaker && (
              <div
                style={{
                  backgroundColor: '#0f172a',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Decision Maker
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                  {detail.decisionMaker.name}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>{detail.decisionMaker.title}</div>
                {detail.decisionMaker.linkedinUrl && (
                  <a
                    href={detail.decisionMaker.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}
                  >
                    LinkedIn ↗
                  </a>
                )}
              </div>
            )}
            {detail.contact && (
              <div
                style={{
                  backgroundColor: '#0f172a',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Contact Email
                </div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', fontFamily: 'monospace' }}>
                  {detail.contact.email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: detail.contact.verified ? '#d1fae5' : '#fef3c7',
                      color: detail.contact.verified ? '#065f46' : '#92400e',
                    }}
                  >
                    {detail.contact.verified ? '✓ Verified' : '~ Unverified'}
                  </span>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {Math.round(detail.contact.confidence * 100)}% confidence
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Why Selected */}
        {detail.qualificationReason && (
          <div style={{ marginTop: '18px', padding: '14px 16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: '6px' }}>
              Why We Selected This Company
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#1e3a8a', lineHeight: 1.6 }}>
              {detail.qualificationReason}
            </p>
          </div>
        )}

        {/* Audit Hypothesis */}
        {detail.auditHypothesis && (
          <div style={{ marginTop: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Audit Hypothesis
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line', padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              {detail.auditHypothesis}
            </p>
          </div>
        )}

        {/* Evidence */}
        {detail.evidence.length > 0 && (
          <div style={{ marginTop: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Evidence ({detail.evidence.length} signals)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {detail.evidence.map((ev, i) => (
                <EvidenceCard key={i} evidence={ev} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Proposed Outreach */}
        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Proposed Outreach
            </div>
            {!editMode && (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
              >
                ✎ Edit
              </button>
            )}
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff' }}>
            {/* Subject */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                Subject
              </div>
              {editMode ? (
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{subject}</div>
              )}
            </div>

            {/* Body */}
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>
                Email Body
              </div>
              {editMode ? (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    lineHeight: 1.7,
                    color: '#374151',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <pre
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#374151',
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                  }}
                >
                  {body}
                </pre>
              )}
            </div>
          </div>

          {editMode && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => void handleEdit()}
                disabled={isBusy}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                }}
              >
                Save Edits
              </button>
              <button
                type="button"
                onClick={() => {
                  if (detail) { setSubject(detail.outreachSubject); setBody(detail.outreachBody); }
                  setEditMode(false);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  color: '#374151',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Reject reason input (only visible when reject modal is open) */}
        {modal === 'reject' && (
          <div style={{ marginTop: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
              Rejection Reason (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. Company is too small, not a fit right now…"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}
      </div>

      {/* Fixed action bar */}
      <div
        style={{
          padding: '16px 28px',
          borderTop: '2px solid #e5e7eb',
          backgroundColor: '#fff',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <ActionButton
          label="✓ APPROVE"
          variant="green"
          onClick={() => setModal('approve')}
          disabled={isBusy}
        />
        <ActionButton
          label="↺ REGENERATE"
          variant="gray"
          onClick={() => void handleRegenerate()}
          disabled={isBusy}
        />
        <div style={{ flex: 1 }} />
        <ActionButton
          label="✗ REJECT"
          variant="red"
          onClick={() => setModal('reject')}
          disabled={isBusy}
        />
        <ActionButton
          label="⊘ SUPPRESS FOREVER"
          variant="darkred"
          onClick={() => setModal('suppress')}
          disabled={isBusy}
        />
      </div>

      {/* Approve modal */}
      <ConfirmModal
        isOpen={modal === 'approve'}
        title="Send this email?"
        message={`You are about to send an email to ${detail.decisionMaker?.name ?? 'the contact'} at ${detail.companyName} (${detail.contact?.email ?? 'unknown email'}). This action cannot be undone.`}
        confirmLabel="Yes, Send Email"
        confirmVariant="primary"
        onConfirm={() => void handleApprove()}
        onCancel={() => setModal(null)}
        isLoading={approve.isPending}
      />

      {/* Reject modal */}
      <ConfirmModal
        isOpen={modal === 'reject'}
        title="Reject this prospect?"
        message={rejectReason.trim() ? `Reason: "${rejectReason}"` : 'Enter a rejection reason in the panel first.'}
        confirmLabel="Confirm Rejection"
        confirmVariant="danger"
        onConfirm={() => {
          if (rejectReason.trim()) void handleReject();
        }}
        onCancel={() => setModal(null)}
        isLoading={reject.isPending}
      />

      {/* Suppress modal */}
      <ConfirmModal
        isOpen={modal === 'suppress'}
        title="Suppress this contact permanently?"
        message={`${detail.contact?.email ?? 'This contact'} at ${detail.companyName} will never be contacted again. This cannot be undone without manual removal.`}
        confirmLabel="Suppress Forever"
        confirmVariant="danger"
        onConfirm={() => void handleSuppress()}
        onCancel={() => setModal(null)}
        isLoading={suppress.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ApprovalQueue() {
  const { data: queue, isLoading, isError } = useApprovalQueue();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = queue ?? [];

  useEffect(() => {
    if (items.length > 0 && !selectedId) {
      setSelectedId(items[0].id);
    }
    if (selectedId && !items.find((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left panel — queue list */}
      <div
        style={{
          width: '320px',
          minWidth: '280px',
          borderRight: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 18px 14px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}
        >
          <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: 800, color: '#111827' }}>
            Approval Queue
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            {isLoading ? 'Loading…' : `${items.length} pending review`}
          </p>
        </div>

        {isError && (
          <div style={{ padding: '12px 16px', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '13px' }}>
            Failed to load queue.
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {items.length === 0 && !isLoading && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎉</div>
              All caught up! No messages pending approval.
            </div>
          )}
          {items.map((item) => (
            <QueueItem
              key={item.id}
              item={item}
              isSelected={item.id === selectedId}
              onClick={() => setSelectedId(item.id)}
            />
          ))}
        </div>
      </div>

      {/* Right panel — detail */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!selectedId ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#9ca3af', fontSize: '15px' }}>
            Select a prospect to review
          </div>
        ) : (
          <DetailPanel
            key={selectedId}
            approvalId={selectedId}
            onDismiss={() => setSelectedId(null)}
          />
        )}
      </div>
    </div>
  );
}
